import React, { useState, useEffect } from 'react';

import {
  MailOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Tag,
  Popconfirm,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '@utils/dateTime';

/**
 * Operator Invitations List Component
 *
 * @param {Object} operatorInvitationService - Operator Invitation Service
 * @param {Boolean} showCard - Wrap table inside a Card
 * @param {Number} refreshKey - Bump to trigger refetch from parent
 */
const OperatorInvitationsList = ({
  operatorInvitationService,
  showCard = true,
  refreshKey = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);

  useEffect(() => {
    fetchInvitations();
  }, [refreshKey]);

  const translateError = (data, fallbackKey) => {
    const firstError = data?.errors?.[0] || {};
    const { detail: errorCode, ...interpolations } = firstError;
    const fallback = t(fallbackKey);
    return errorCode
      ? t(`shared.operator_invitations.error.${errorCode}`, { ...interpolations, defaultValue: fallback })
      : fallback;
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const data = await operatorInvitationService.fetchList();

      if (data.success && data.data) {
        setInvitations(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      message.error(t('shared.operator_invitations.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    message.success(t('shared.operator_invitations.messages.link_copied'));
  };

  const handleRegenerate = async (id) => {
    try {
      const data = await operatorInvitationService.regenerate(id);

      if (data.success) {
        message.success(t('shared.operator_invitations.messages.regenerate_success'));
        fetchInvitations();
      } else {
        message.error(translateError(data, 'shared.operator_invitations.error.regenerate_failed'));
      }
    } catch (error) {
      console.error('Failed to regenerate invitation:', error);
      message.error(t('shared.operator_invitations.error.regenerate_failed'));
    }
  };

  const handleDelete = async (id) => {
    try {
      const data = await operatorInvitationService.delete(id);

      if (data.success) {
        message.success(t('shared.operator_invitations.messages.delete_success'));
        fetchInvitations();
      } else {
        message.error(translateError(data, 'shared.operator_invitations.error.delete_failed'));
      }
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      message.error(t('shared.operator_invitations.error.delete_failed'));
    }
  };

  const getStatusTag = (record) => {
    if (record.status === 'accepted') {
      return <Tag icon={<CheckCircleOutlined />} color="success">{t('enums.operator_invitation.status.accepted')}</Tag>;
    }
    if (record.expired) {
      return <Tag icon={<CloseCircleOutlined />} color="error">{t('enums.operator_invitation.status.expired')}</Tag>;
    }
    if (record.status === 'canceled') {
      return <Tag icon={<CloseCircleOutlined />} color="default">{t('enums.operator_invitation.status.canceled')}</Tag>;
    }
    return <Tag icon={<ClockCircleOutlined />} color="processing">{t('enums.operator_invitation.status.pending')}</Tag>;
  };

  const columns = [
    {
      title: t('models.operator_invitation.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('models.operator_invitation.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('models.operator_invitation.warehouse'),
      key: 'warehouse',
      render: (_, record) => (
        <Space>
          <ShopOutlined />
          {record.warehouse_name || '-'}
        </Space>
      ),
    },
    {
      title: t('models.operator_invitation.status'),
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: t('common.audit.inviter', { defaultValue: '邀請人' }),
      dataIndex: 'inviter',
      key: 'inviter',
      render: (inviter) => inviter?.name || '-',
      width: '120px',
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => formatDateTime(text),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status === 'pending' && !record.expired && (
            <>
              <Tooltip title={t('common.button.copy_link')}>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyLink(record.invitation_url)}
                />
              </Tooltip>
              <Tooltip title={t('shared.operator_invitations.actions.regenerate')}>
                <Popconfirm
                  title={t('shared.operator_invitations.confirm.regenerate')}
                  onConfirm={() => handleRegenerate(record.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<ReloadOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
              <Tooltip title={t('common.button.cancel')}>
                <Popconfirm
                  title={t('shared.operator_invitations.confirm.delete')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Tooltip>
            </>
          )}
          {record.status === 'accepted' && record.operator && (
            <span style={{ color: '#999', fontSize: '12px' }}>
              {record.operator.name}
            </span>
          )}
        </Space>
      ),
    },
  ];

  const tableContent = (
    <Table
      dataSource={invitations}
      columns={columns}
      rowKey="id"
      loading={loading}
      pagination={{ pageSize: 10 }}
    />
  );

  if (showCard) {
    return (
      <Card
        title={
          <Space>
            <MailOutlined />
            {t('shared.operator_invitations.title')}
          </Space>
        }
      >
        {tableContent}
      </Card>
    );
  }

  return tableContent;
};

export default OperatorInvitationsList;
